import React, { useState, useEffect } from 'react';
import { clubApi } from '../services/api';

const COURT_TYPES = ['badminton', 'tennis', 'table_tennis', 'squash', 'multi'];

const ClubManagement = () => {
  const [clubs, setClubs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedClub, setExpandedClub] = useState(null);
  const [courts, setCourts] = useState({}); // clubId -> court[]

  // Club form state
  const [showClubForm, setShowClubForm] = useState(false);
  const [editingClub, setEditingClub] = useState(null);
  const [clubForm, setClubForm] = useState({ name: '', address: '', contact_email: '', contact_phone: '' });
  const [savingClub, setSavingClub] = useState(false);

  // Court form state
  const [showCourtForm, setShowCourtForm] = useState(null); // clubId or null
  const [editingCourt, setEditingCourt] = useState(null);
  const [courtForm, setCourtForm] = useState({ name: '', court_type: 'badminton' });
  const [savingCourt, setSavingCourt] = useState(false);

  const loadClubs = async () => {
    try {
      const res = await clubApi.getAll();
      setClubs(res.data);
    } catch (err) {
      alert('Failed to load clubs');
    } finally {
      setLoading(false);
    }
  };

  const loadCourts = async (clubId) => {
    try {
      const res = await clubApi.getCourts(clubId);
      setCourts(prev => ({ ...prev, [clubId]: res.data }));
    } catch (err) {
      console.error('Failed to load courts', err);
    }
  };

  useEffect(() => { loadClubs(); }, []);

  const handleExpandClub = (clubId) => {
    if (expandedClub === clubId) {
      setExpandedClub(null);
    } else {
      setExpandedClub(clubId);
      if (!courts[clubId]) loadCourts(clubId);
    }
  };

  const openAddClub = () => {
    setEditingClub(null);
    setClubForm({ name: '', address: '', contact_email: '', contact_phone: '' });
    setShowClubForm(true);
  };

  const openEditClub = (club) => {
    setEditingClub(club);
    setClubForm({ name: club.name, address: club.address || '', contact_email: club.contact_email || '', contact_phone: club.contact_phone || '' });
    setShowClubForm(true);
  };

  const handleSaveClub = async (e) => {
    e.preventDefault();
    setSavingClub(true);
    try {
      if (editingClub) {
        await clubApi.update(editingClub.id, clubForm);
      } else {
        await clubApi.create(clubForm);
      }
      setShowClubForm(false);
      setEditingClub(null);
      await loadClubs();
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to save club');
    } finally {
      setSavingClub(false);
    }
  };

  const handleDeleteClub = async (clubId) => {
    if (!window.confirm('Delete this club and all its courts?')) return;
    try {
      await clubApi.delete(clubId);
      setClubs(prev => prev.filter(c => c.id !== clubId));
      if (expandedClub === clubId) setExpandedClub(null);
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to delete club');
    }
  };

  const openAddCourt = (clubId) => {
    setEditingCourt(null);
    setCourtForm({ name: '', court_type: 'badminton' });
    setShowCourtForm(clubId);
  };

  const openEditCourt = (court, clubId) => {
    setEditingCourt(court);
    setCourtForm({ name: court.name, court_type: court.court_type || 'badminton' });
    setShowCourtForm(clubId);
  };

  const handleSaveCourt = async (e) => {
    e.preventDefault();
    setSavingCourt(true);
    const clubId = showCourtForm;
    try {
      if (editingCourt) {
        await clubApi.updateCourt(clubId, editingCourt.id, courtForm);
      } else {
        await clubApi.createCourt(clubId, courtForm);
      }
      setShowCourtForm(null);
      setEditingCourt(null);
      await loadCourts(clubId);
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to save court');
    } finally {
      setSavingCourt(false);
    }
  };

  const handleDeleteCourt = async (clubId, courtId) => {
    if (!window.confirm('Delete this court?')) return;
    try {
      await clubApi.deleteCourt(clubId, courtId);
      setCourts(prev => ({ ...prev, [clubId]: (prev[clubId] || []).filter(c => c.id !== courtId) }));
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to delete court');
    }
  };

  const toggleCourtAvailability = async (clubId, court) => {
    try {
      const updated = await clubApi.updateCourt(clubId, court.id, { is_available: !court.is_available });
      setCourts(prev => ({
        ...prev,
        [clubId]: (prev[clubId] || []).map(c => c.id === court.id ? updated.data : c)
      }));
    } catch (err) {
      alert('Failed to update court availability');
    }
  };

  if (loading) return <div className="container"><div className="loading"><div className="spinner"></div></div></div>;

  return (
    <div className="container">
      <div className="page-header">
        <div>
          <h1 className="page-title">Club & Court Management</h1>
          <p style={{ color: 'var(--text-secondary)', marginTop: '0.25rem' }}>Manage clubs and their courts</p>
        </div>
        <button className="btn btn-primary" onClick={openAddClub}>+ Add Club</button>
      </div>

      {clubs.length === 0 ? (
        <div className="card">
          <div className="empty-state">
            <p>No clubs yet. Add your first club to get started.</p>
          </div>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
          {clubs.map(club => (
            <div key={club.id} className="card" style={{ padding: 0 }}>
              {/* Club header */}
              <div
                style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '1rem 1.25rem', cursor: 'pointer' }}
                onClick={() => handleExpandClub(club.id)}
              >
                <div>
                  <div style={{ fontWeight: '600', fontSize: '1.05rem' }}>{club.name}</div>
                  <div style={{ color: 'var(--text-secondary)', fontSize: '0.85rem', marginTop: '2px' }}>
                    {[club.address, club.contact_email, club.contact_phone].filter(Boolean).join(' · ') || 'No contact info'}
                  </div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <button
                    className="btn btn-sm btn-outline"
                    onClick={e => { e.stopPropagation(); openEditClub(club); }}
                  >Edit</button>
                  <button
                    className="btn btn-sm btn-outline"
                    style={{ color: '#dc3545', borderColor: '#dc3545' }}
                    onClick={e => { e.stopPropagation(); handleDeleteClub(club.id); }}
                  >Delete</button>
                  <span style={{ color: 'var(--text-secondary)', fontSize: '1.1rem' }}>
                    {expandedClub === club.id ? '▲' : '▼'}
                  </span>
                </div>
              </div>

              {/* Courts panel */}
              {expandedClub === club.id && (
                <div style={{ borderTop: '1px solid var(--border-color)', padding: '1rem 1.25rem' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem' }}>
                    <h4 style={{ margin: 0, color: 'var(--text-secondary)' }}>Courts</h4>
                    <button className="btn btn-sm btn-primary" onClick={() => openAddCourt(club.id)}>+ Add Court</button>
                  </div>

                  {!courts[club.id] ? (
                    <p style={{ color: 'var(--text-secondary)', fontSize: '0.9rem' }}>Loading courts...</p>
                  ) : courts[club.id].length === 0 ? (
                    <p style={{ color: 'var(--text-secondary)', fontSize: '0.9rem' }}>No courts yet.</p>
                  ) : (
                    <div className="table-container">
                      <table className="table">
                        <thead>
                          <tr>
                            <th>Name</th>
                            <th>Type</th>
                            <th>Available</th>
                            <th>Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {courts[club.id].map(court => (
                            <tr key={court.id}>
                              <td>{court.name}</td>
                              <td style={{ textTransform: 'capitalize' }}>{court.court_type?.replace(/_/g, ' ')}</td>
                              <td>
                                <button
                                  className={`badge ${court.is_available ? 'badge-success' : 'badge-error'}`}
                                  style={{ border: 'none', cursor: 'pointer', background: court.is_available ? '#d4edda' : '#f8d7da', color: court.is_available ? '#155724' : '#721c24' }}
                                  onClick={() => toggleCourtAvailability(club.id, court)}
                                  title="Click to toggle"
                                >
                                  {court.is_available ? 'Available' : 'Unavailable'}
                                </button>
                              </td>
                              <td>
                                <div style={{ display: 'flex', gap: '0.4rem' }}>
                                  <button className="btn btn-sm btn-outline" onClick={() => openEditCourt(court, club.id)}>Edit</button>
                                  <button
                                    className="btn btn-sm btn-outline"
                                    style={{ color: '#dc3545', borderColor: '#dc3545' }}
                                    onClick={() => handleDeleteCourt(club.id, court.id)}
                                  >Delete</button>
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Club form modal */}
      {showClubForm && (
        <div className="modal-overlay" onClick={() => setShowClubForm(false)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <h3>{editingClub ? 'Edit Club' : 'Add Club'}</h3>
            <form onSubmit={handleSaveClub}>
              <div className="form-group">
                <label className="form-label">Club Name *</label>
                <input type="text" className="form-input" value={clubForm.name} onChange={e => setClubForm({ ...clubForm, name: e.target.value })} required />
              </div>
              <div className="form-group">
                <label className="form-label">Address</label>
                <input type="text" className="form-input" value={clubForm.address} onChange={e => setClubForm({ ...clubForm, address: e.target.value })} />
              </div>
              <div className="grid grid-2">
                <div className="form-group">
                  <label className="form-label">Contact Email</label>
                  <input type="email" className="form-input" value={clubForm.contact_email} onChange={e => setClubForm({ ...clubForm, contact_email: e.target.value })} />
                </div>
                <div className="form-group">
                  <label className="form-label">Contact Phone</label>
                  <input type="text" className="form-input" value={clubForm.contact_phone} onChange={e => setClubForm({ ...clubForm, contact_phone: e.target.value })} />
                </div>
              </div>
              <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end', marginTop: '1rem' }}>
                <button type="button" className="btn btn-outline" onClick={() => setShowClubForm(false)}>Cancel</button>
                <button type="submit" className="btn btn-primary" disabled={savingClub}>{savingClub ? 'Saving...' : 'Save'}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Court form modal */}
      {showCourtForm && (
        <div className="modal-overlay" onClick={() => setShowCourtForm(null)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <h3>{editingCourt ? 'Edit Court' : 'Add Court'}</h3>
            <form onSubmit={handleSaveCourt}>
              <div className="form-group">
                <label className="form-label">Court Name *</label>
                <input type="text" className="form-input" value={courtForm.name} onChange={e => setCourtForm({ ...courtForm, name: e.target.value })} required placeholder="e.g. Court 1, Center Court" />
              </div>
              <div className="form-group">
                <label className="form-label">Court Type</label>
                <select className="form-select" value={courtForm.court_type} onChange={e => setCourtForm({ ...courtForm, court_type: e.target.value })}>
                  {COURT_TYPES.map(t => (
                    <option key={t} value={t}>{t.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}</option>
                  ))}
                </select>
              </div>
              <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end', marginTop: '1rem' }}>
                <button type="button" className="btn btn-outline" onClick={() => setShowCourtForm(null)}>Cancel</button>
                <button type="submit" className="btn btn-primary" disabled={savingCourt}>{savingCourt ? 'Saving...' : 'Save'}</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default ClubManagement;
